import axios from 'axios'
import { isLikelyOazapftsHttpError } from '@/lib/sentry-http-error'

export const ApiErrorCode = {
  BATCH_IMPORT_LOCK_CONFLICT: 'BATCH_IMPORT_LOCK_CONFLICT',
  BATCH_IMPORT_LOCK_LOST: 'BATCH_IMPORT_LOCK_LOST',
  EXPORT_EDIT_LOCK_REQUIRED: 'EXPORT_EDIT_LOCK_REQUIRED',
} as const

export type ApiErrorCodeValue =
  (typeof ApiErrorCode)[keyof typeof ApiErrorCode]

export type ApiErrorDetails = {
  lockedBy?: string
  lockedAt?: string
  takenOverBy?: string
  [key: string]: unknown
}

type GatewayErrorBody = {
  errorMessage?: string
  errorHumanMessage?: string
  traceId?: string
  details?: ApiErrorDetails
}

export function parseErrorBodyData(
  data: unknown
): GatewayErrorBody | undefined {
  if (data && typeof data === 'object') {
    const body = data as GatewayErrorBody & { data?: unknown; code?: string }
    if (body.data && typeof body.data === 'object') {
      return { ...body, ...(body.data as GatewayErrorBody) }
    }
    return body
  }

  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data)
      if (parsed && typeof parsed === 'object') {
        return parsed as GatewayErrorBody
      }
    } catch {
      /* not JSON */
    }
  }

  return undefined
}

function getErrorBody(error: unknown): GatewayErrorBody | undefined {
  if (axios.isAxiosError(error)) {
    return parseErrorBodyData(error.response?.data)
  }

  const axiosLikeBody = getAxiosLikeResponseStatus(error)
    ? parseErrorBodyData(
        (error as { response?: { data?: unknown } }).response?.data
      )
    : undefined
  if (axiosLikeBody) {
    return axiosLikeBody
  }

  if (isLikelyOazapftsHttpError(error)) {
    const data = error.data
    if (data && typeof data === 'object') {
      return data as GatewayErrorBody
    }
    return undefined
  }

  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data
    if (data && typeof data === 'object') {
      return data as GatewayErrorBody
    }
  }

  return undefined
}

function getAxiosLikeResponseStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined
  }
  const status = (error as { response?: { status?: unknown } }).response?.status
  return typeof status === 'number' ? status : undefined
}

export function getApiErrorStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status
  }

  const axiosLikeStatus = getAxiosLikeResponseStatus(error)
  if (axiosLikeStatus !== undefined) {
    return axiosLikeStatus
  }

  if (isLikelyOazapftsHttpError(error)) {
    return error.status
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status
    if (typeof status === 'number') {
      return status
    }
  }

  return undefined
}

export function getApiErrorCode(error: unknown): string | undefined {
  const body = getErrorBody(error)
  if (body?.errorMessage && typeof body.errorMessage === 'string') {
    return body.errorMessage
  }
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code?: unknown }).code
    if (typeof code === 'string') {
      return code
    }
  }
  return undefined
}

export function isEditLockLostError(error: unknown): boolean {
  const status = getApiErrorStatus(error)
  if (status === 423) {
    return true
  }
  return isApiErrorCode(error, ApiErrorCode.BATCH_IMPORT_LOCK_LOST)
}

export function isExportEditLockRequiredError(error: unknown): boolean {
  return isApiErrorCode(error, ApiErrorCode.EXPORT_EDIT_LOCK_REQUIRED)
}

export function isLockConflictError(error: unknown): boolean {
  const status = getApiErrorStatus(error)
  if (status === 409) {
    return true
  }
  return isApiErrorCode(error, ApiErrorCode.BATCH_IMPORT_LOCK_CONFLICT)
}

/** Le backend peut renvoyer 500 au lieu de 423 quand le verrou est perdu. */
export function shouldRevalidateEditLockAfterMutationError(
  error: unknown
): boolean {
  const status = getApiErrorStatus(error)
  if (status === 500) {
    return true
  }
  if (isLockConflictError(error)) {
    return true
  }
  if (status === 403) {
    return true
  }
  return false
}

export function getApiErrorDetails(error: unknown): ApiErrorDetails | undefined {
  const body = getErrorBody(error)
  if (body?.details && typeof body.details === 'object') {
    return body.details as ApiErrorDetails
  }
  return undefined
}

export function isApiErrorCode(
  error: unknown,
  code: ApiErrorCodeValue
): boolean {
  return getApiErrorCode(error) === code
}
